// src/eventsettings/eventpreview.jsx
// PDFのプレビュー画面（管理者・主催者で共通）：冊子を見開きで表示する。
// basePath は、設定画面に戻るリンクの行き先を決める（"/admin" または "/operator"）。
import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import PdfBookView from "./eventpdfpreview";
import "./eventsettings.css";

const API_URL = import.meta.env.VITE_API_URL;

// ファイル名に使えない文字を「_」に置き換える
const safeFileName = (s) => String(s || "").replace(/[\\/:*?"<>|]/g, "_").trim();

// blob で受け取ったエラー応答（JSON）から、サーバーのメッセージを取り出す
const errorMessage = async (err, fallback) => {
  try {
    const text = await err.response?.data?.text?.();
    return JSON.parse(text)?.message || fallback;
  } catch {
    return fallback;
  }
};

export default function EventPreview({ basePath }) {
  const { eventId } = useParams();
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  // ダウンロードするファイル名に使う、イベント名（設定画面と同じキー）
  const { data: settings } = useQuery({
    queryKey: ["event-settings", eventId],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/events/${eventId}/settings/`);
      if (!res.data?.success) throw new Error("イベント設定の取得に失敗しました");
      return res.data.data;
    },
  });

  // 冊子全体を、1ページ1枚のPDFにして、ダウンロードする
  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const res = await axios.get(`${API_URL}/api/events/${eventId}/booklet-pdf/`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeFileName(settings?.event?.title) || `イベント${eventId}`}_冊子.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setExportError(await errorMessage(err, "PDFを出力できませんでした"));
    } finally {
      setExporting(false);
    }
  };

  // 表用・裏用の一覧（PDF設定タブと同じキー。追加・削除のあとの最新の内容を使う）
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["event-pdfs", eventId],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/events/${eventId}/pdfs/`);
      if (!res.data?.success) throw new Error("PDF設定の取得に失敗しました");
      return res.data.data;
    },
    staleTime: 0,
    refetchOnMount: "always",
  });

  return (
    <div className="evSetPage evSetPreviewPage">
      <div className="evSetPreviewBar">
        <Link to={`${basePath}/events/${eventId}/settings`} className="evSetBack">
          <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 6l-6 6 6 6" />
          </svg>
          設定に戻る
        </Link>
        <p className="evSetSub">PDFのプレビュー</p>

        <div className="evSetActions">
          <button
            type="button"
            className="evSetBtn isPrimary"
            disabled={exporting}
            onClick={handleExport}
          >
            {exporting ? "作成中..." : "PDFを出力"}
          </button>
          <span className="evSetPdfMeta">1ページ1枚のPDFで保存します</span>
          {exportError && <span className="evSetMsg isError">{exportError}</span>}
        </div>
      </div>

      {isLoading && <div className="evSetLoading">読み込み中...</div>}
      {isError && (
        <div className="evSetMsg isError">
          {error?.response?.status === 404
            ? "イベントが見つかりません"
            : `エラー: ${error?.message || "取得に失敗しました"}`}
        </div>
      )}
      {data && <PdfBookView eventId={eventId} front={data.front} back={data.back} />}
    </div>
  );
}
